<script setup lang="ts">
import {
  BillingCycle,
  BillingCycleLabelMap,
  BillingCycleDescriptionMap,
} from "~/types/app";

/**
 * Accounts created on or after this date selected their billing cycle during
 * onboarding, so they already know about it and don't need to be notified.
 */
const CUTOFF_DATE = new Date("2026-08-01T00:00:00Z");
const STOP_SHOWING_MESSAGE_DATE = new Date("2026-08-15T00:00:00Z");

const auth = useAuth();

const isOpen = ref(false);

const { invoiceFeatureGenerallyAvailable } = useFeatureFlags();
const billingCycle = computed(() => auth.account?.billing_cycle ?? null);

const billingCycleLabel = computed(() =>
  billingCycle.value ? BillingCycleLabelMap[billingCycle.value] : "",
);

const billingCycleDescription = computed(() =>
  billingCycle.value ? BillingCycleDescriptionMap[billingCycle.value] : "",
);

// Fixed fee is reserved for specific high-volume accounts and can only be
// changed by contacting support.
const isFixedFee = computed(
  () => billingCycle.value === BillingCycle.FIXED_FEE,
);

/**
 * Per-account key so the alert is dismissed independently for each account.
 */
const storageKey = computed(() =>
  auth.account?.id
    ? `billing-cycle-alert-dismissed:${auth.account.id}`
    : null,
);

const shouldShow = (): boolean => {
  const account = auth.account;

  if (!invoiceFeatureGenerallyAvailable) {
    return false;
  }

  // Only account owners can manage the billing cycle.
  if (!auth.isOwner()) {
    return false;
  }

  // No account, or the billing cycle param is not set -> don't show.
  if (!account?.id || !account?.billing_cycle || !account?.created_at) {
    return false;
  }

  if (new Date(account.created_at) >= CUTOFF_DATE || new Date() >= STOP_SHOWING_MESSAGE_DATE) {
    return false;
  }

  // Already acknowledged on this device.
  if (storageKey.value && localStorage.getItem(storageKey.value) === "true") {
    return false;
  }

  return true;
};

// Force the user to read the notice for a few seconds before they can dismiss.
const DISMISS_DELAY_SECONDS = 5;
const secondsLeft = ref(DISMISS_DELAY_SECONDS);
const canDismiss = computed(() => secondsLeft.value <= 0);
let countdownTimer: ReturnType<typeof setInterval> | null = null;

const clearCountdown = () => {
  if (countdownTimer) {
    clearInterval(countdownTimer);
    countdownTimer = null;
  }
};

const startCountdown = () => {
  clearCountdown();
  secondsLeft.value = DISMISS_DELAY_SECONDS;
  countdownTimer = setInterval(() => {
    secondsLeft.value -= 1;
    if (secondsLeft.value <= 0) {
      clearCountdown();
    }
  }, 1000);
};

const dismiss = () => {
  // Ignore attempts to close before the countdown finishes.
  if (!canDismiss.value) return;
  if (storageKey.value) {
    localStorage.setItem(storageKey.value, "true");
  }
  isOpen.value = false;
};

const onOpenChange = (open: boolean) => {
  // Keep the modal open until the user is allowed to dismiss it.
  if (!open && !canDismiss.value) return;
  isOpen.value = open;
  if (!open) dismiss();
};

// Block the links (mouse or keyboard) until the countdown finishes.
const onLinkClick = (event: MouseEvent) => {
  if (!canDismiss.value) {
    event.preventDefault();
    return;
  }
  dismiss();
};

onUnmounted(clearCountdown);

// The account is loaded asynchronously, so react once it becomes available.
watch(
  () => [auth.account, auth.user],
  () => {
    if (shouldShow()) {
      isOpen.value = true;
      startCountdown();
    }
  },
  { immediate: true, deep: true },
);
</script>

<template>
  <CommonAlert :model-value="isOpen" :show-cancel="false" :disabled="!canDismiss" data-test-id="billing-cycle-alert"
    @update:confirm="dismiss" @update:model-value="onOpenChange">
    <template #title>We're moving to automated invoicing</template>
    <template #content>
      <p>
        To make billing simpler, we're transitioning to automated invoices.
        Based on your account, your billing cycle has been set to
        <strong>{{ billingCycleLabel }}</strong>.
      </p>
      <p v-if="billingCycleDescription" class="mt-2">
        {{ billingCycleDescription }}
      </p>
      <p v-if="isFixedFee" class="mt-3">
        To change your billing cycle, please contact
        <a href="mailto:support@notary.io" class="text-ngreen font-semibold underline hover:opacity-80" data-test-id="billing-cycle-alert-support-link">
          support@notary.io
        </a>
      </p>
      <p v-else class="mt-3">
        You can change this anytime from your
        <NuxtLink to="/hub/account?selected-tab=tab3" class="text-ngreen font-semibold underline hover:opacity-80"
          :class="{ 'pointer-events-none opacity-50': !canDismiss }" data-test-id="billing-cycle-alert-manage-link"
          @click="onLinkClick">Invoicing settings.</NuxtLink>
      </p>
    </template>
    <template #confirm>
      {{ canDismiss ? "Got it" : `Got it (${secondsLeft})` }}
    </template>
  </CommonAlert>
</template>

<style scoped></style>
